import type { MbcBriefQuality } from "@nlc/shared-types";

import { ChevronRight, Paperclip } from "lucide-react";
import { useMemo, useState } from "react";

import { Sheet, SheetContent } from "@/components/sheet";
import { BriefQualityCard } from "@/features/request/mbc-review/components/BriefQualityCard";
import { briefScoreSummary } from "@/features/request/mbc-review/lib/brief-score-summary";

import type { BriefMemory } from "../hooks/useBriefMemory";

import { collectFiles } from "../lib/collect-files";
import { UploadedFilesPanel } from "./UploadedFilesPanel";

type MBCBriefSheetProps = {
  canAddFiles: boolean;
  isCalculating: boolean;
  memory: BriefMemory | null;
  onAddAssets: () => void;
  onFilesChanged: (target: "assets" | "inspirations") => void;
  quality: MbcBriefQuality | null;
};

export function MBCBriefSheet({
  canAddFiles,
  isCalculating,
  memory,
  onAddAssets,
  onFilesChanged,
  quality,
}: MBCBriefSheetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { color, isJudging, score } = briefScoreSummary(quality);
  const fileCount = useMemo(
    () =>
      collectFiles(memory?.assets).length +
      collectFiles(memory?.inspirations).length,
    [memory],
  );

  return (
    <div className="border-b border-border-secondary bg-bg-primary lg:hidden">
      <button
        aria-label="View brief"
        className="flex w-full items-center gap-md px-md py-sm text-sm outline-hidden transition-colors hover:bg-bg-primary-hover"
        onClick={() => setIsOpen(true)}
        type="button"
      >
        <span className="font-medium text-text-secondary">Brief quality</span>
        <span className="font-bold" style={{ color }}>
          {score === null || isCalculating || isJudging ? "--" : `${score}%`}
        </span>
        <span className="ml-auto flex items-center gap-xs text-text-tertiary">
          <Paperclip className="size-4" />
          {fileCount}
        </span>
        <ChevronRight className="size-4 text-fg-quinary" />
      </button>

      <Sheet
        classNames={{ content: "w-[min(360px,90vw)]" }}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        side="right"
      >
        <SheetContent
          aria-label="Brief"
          className="flex h-full flex-col gap-xl overflow-y-auto bg-bg-secondary p-md outline-hidden"
        >
          <BriefQualityCard
            className="shrink-0 border-border-secondary shadow-none"
            isCalculating={isCalculating}
            quality={quality}
          />
          <UploadedFilesPanel
            canAddFiles={canAddFiles}
            memory={memory}
            onAddAssets={() => {
              setIsOpen(false);
              onAddAssets();
            }}
            onFilesChanged={onFilesChanged}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
